/**
 * Story 8.12 — Modale profil membre du groupement (lecture seule)
 *
 * Affiche le profil complet d'un cotraitant dans une modale avec :
 * - En-tête : logo, nom, SIRET, représentant, mail, tél, progression
 * - 5 onglets en lecture seule : Présentation, Documents, Références, Bancaire, Équipe
 *
 * Fermable via × ou clic extérieur.
 */
import React, { useState } from 'react';
import TabNav from '../../../../composants/TabNav/TabNav';
import { ProfilProgressBar } from './SectionCandidat';
import ReadOnlyPresentation from './readonly/ReadOnlyPresentation';
import ReadOnlyDocuments from './readonly/ReadOnlyDocuments';
import ReadOnlyReferences from './readonly/ReadOnlyReferences';
import ReadOnlyBancaire from './readonly/ReadOnlyBancaire';
import ReadOnlyEquipe from './readonly/ReadOnlyEquipe';
import type { ProfileData } from '../../../../services/profileService';
import './ModalProfilMembre.css';

interface ModalProfilMembreProps {
    isOpen: boolean;
    onClose: () => void;
    profile: ProfileData | null;
    isLoading: boolean;
    error: string | null;
}

const MEMBER_TABS = ['Présentation', 'Documents', 'Références', 'Bancaire', 'Gestion d\'équipe'];

const ModalProfilMembre: React.FC<ModalProfilMembreProps> = ({
    isOpen,
    onClose,
    profile,
    isLoading,
    error,
}) => {
    const [activeTab, setActiveTab] = useState(0);

    if (!isOpen) return null;

    return (
        <div className="modal-backdrop modal-profil-backdrop" onClick={onClose}>
            <div className="modal-profil-membre" onClick={(e) => e.stopPropagation()}>
                {/* Bouton fermer */}
                <button className="modal-close-btn" onClick={onClose} aria-label="Fermer">
                    ×
                </button>

                {/* Loading */}
                {isLoading && (
                    <div className="modal-profil-loading">
                        <div className="spinner" />
                        <p>Chargement du profil…</p>
                    </div>
                )}

                {/* Erreur */}
                {error && !isLoading && (
                    <div className="modal-profil-error">
                        <p>{error}</p>
                    </div>
                )}

                {/* Contenu profil */}
                {profile && !isLoading && !error && (
                    <>
                        {/* En-tête profil */}
                        <div className="modal-profil-header">
                            <div className="modal-profil-header-left">
                                {profile.entite.logo_url ? (
                                    <img
                                        src={profile.entite.logo_url}
                                        alt={`Logo ${profile.entite.nom}`}
                                        className="modal-profil-logo"
                                    />
                                ) : (
                                    <div className="modal-profil-logo-placeholder">
                                        {(profile.entite.nom || '?')[0].toUpperCase()}
                                    </div>
                                )}
                                <div className="modal-profil-identity">
                                    <h2>{profile.entite.nom}</h2>
                                    <div className="modal-profil-details">
                                        {profile.entite.siret && (
                                            <span>SIRET : {profile.entite.siret}</span>
                                        )}
                                        {profile.coordonnees?.legal_nom && profile.coordonnees?.legal_prenom && (
                                            <span>Représentant : {profile.coordonnees.legal_prenom} {profile.coordonnees.legal_nom}</span>
                                        )}
                                        {profile.coordonnees?.legal_email && (
                                            <span>Mail : {profile.coordonnees.legal_email}</span>
                                        )}
                                        {profile.coordonnees?.legal_tel && (
                                            <span>Tél : {profile.coordonnees.legal_tel}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-profil-header-right">
                                <ProfilProgressBar value={profile.entite.progression ?? 0} />
                            </div>
                        </div>

                        {/* Onglets */}
                        <div className="modal-profil-tabs">
                            <TabNav
                                tabs={MEMBER_TABS}
                                activeTab={activeTab}
                                onTabChange={setActiveTab}
                            />
                        </div>

                        {/* Contenu onglet */}
                        <div className="modal-profil-body">
                            {activeTab === 0 && <ReadOnlyPresentation data={profile} />}
                            {activeTab === 1 && <ReadOnlyDocuments data={profile} />}
                            {activeTab === 2 && <ReadOnlyReferences data={profile} />}
                            {activeTab === 3 && <ReadOnlyBancaire data={profile} />}
                            {activeTab === 4 && <ReadOnlyEquipe data={profile} />}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ModalProfilMembre;
